/**
 * Lift2Fit — auth + role state for the web app.
 *
 * One source of truth in localStorage (`l2f.session`) shared by every screen.
 * The demo role switcher is part of the same store: without a backend a reviewer
 * can flip between lifter / trainer / coach and between training tracks, and the
 * dashboard re-renders against the demo dataset honestly labelled as demo data.
 */
import { useEffect, useState } from 'react';
import {
  api,
  normalizeUser,
  setAuthToken,
  subscribeSource,
  type AuthResponse,
  type RegisterInput,
  type Role,
  type TrainingTrack,
  type User,
} from './api';
import { DEMO_LIFTER, DEMO_TRAINER, DEMO_COACH, demoMe, setDemoTrack, setDemoUserId, trackMeta } from '../app/demoData';

const STORAGE_KEY = 'l2f.session';

export interface SessionState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  source: 'api' | 'demo' | null;
}

const EMPTY: SessionState = { user: null, accessToken: null, refreshToken: null, source: null };

function isUser(value: unknown): value is User {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<User>;
  return typeof candidate.id === 'string' && Array.isArray(candidate.roles) && typeof candidate.email === 'string';
}

export function readSession(): SessionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<SessionState>;
    if (!isUser(parsed.user)) return EMPTY;
    return {
      // A real account payload may omit track/trackLabel and other demo-only
      // fields; normalising on read keeps every screen's assumptions true.
      user: normalizeUser(parsed.user),
      accessToken: typeof parsed.accessToken === 'string' ? parsed.accessToken : null,
      refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : null,
      source: parsed.source === 'api' || parsed.source === 'demo' ? parsed.source : null,
    };
  } catch {
    return EMPTY;
  }
}

const listeners = new Set<(session: SessionState) => void>();

function publish(next: SessionState): void {
  try {
    if (next.user) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked — the session still works for this tab */
  }
  setAuthToken(next.accessToken);
  if (next.user?.track) setDemoTrack(next.user.track);
  // The fallback block/goal dataset mirrors the account: a fresh signup sees an
  // empty block, the seeded persona sees the demo history.
  setDemoUserId(next.user?.id ?? null);
  for (const listener of listeners) listener(next);
}

export function hasRole(user: User | null | undefined, role: Role): boolean {
  return Boolean(user?.roles?.includes(role));
}

export function hasAnyRole(user: User | null | undefined, roles: Role[]): boolean {
  return roles.some((role) => hasRole(user, role));
}

export interface SessionApi {
  session: SessionState;
  user: User | null;
  source: 'api' | 'demo' | null;
  isDemo: boolean;
  isAuthenticated: boolean;
  hasRole: (role: Role) => boolean;
  /** 'lifter' | 'trainer' | 'coach' — the highest-privilege role held. */
  primaryRole: Role;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  register: (input: RegisterInput) => Promise<{ ok: boolean; error?: string }>;
  loginDemo: (role?: Role) => void;
  switchRole: (role: Role) => void;
  switchTrack: (track: TrainingTrack) => void;
  updateUser: (patch: Partial<User>) => void;
  logout: () => void;
}

function toState(result: { data: AuthResponse | null; source: 'api' | 'demo' }): SessionState | null {
  if (!result.data) return null;
  return {
    user: result.data.user,
    accessToken: result.data.accessToken,
    refreshToken: result.data.refreshToken,
    source: result.source,
  };
}

function primaryRoleOf(user: User | null): Role {
  if (hasRole(user, 'coach')) return 'coach';
  if (hasRole(user, 'trainer')) return 'trainer';
  return 'lifter';
}

/** Seed a demo session for a role; roles are additive, exactly like the contract. */
function demoSessionFor(role: Role): SessionState {
  const user = demoMe(role);
  return { user, accessToken: 'demo-access-token', refreshToken: 'demo-refresh-token', source: 'demo' };
}

/** Boot once so a hard refresh keeps working before React mounts. */
const bootSession = readSession();
setAuthToken(bootSession.accessToken);
if (bootSession.user) setDemoTrack(bootSession.user.track);
setDemoUserId(bootSession.user?.id ?? null);

export function useSession(): SessionApi {
  const [session, setSession] = useState<SessionState>(readSession);

  useEffect(() => {
    const listener = (next: SessionState) => setSession(next);
    listeners.add(listener);
    const unsubscribe = subscribeSource((source) => {
      // `source` is how this account signed in, not the outcome of the last
      // fetch: a real API session is never relabelled "demo" because one screen
      // fell back (the blocks API, for instance, may not be deployed yet).
      setSession((prev) => (prev.user && prev.source !== 'api' ? { ...prev, source } : prev));
    });
    return () => {
      listeners.delete(listener);
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const result = await api.login(email, password);
    if (!result.data) {
      return { ok: false, error: result.problem?.detail ?? result.problem?.title ?? 'Sign in failed' };
    }
    const next = toState(result);
    if (next) publish(next);
    return { ok: true };
  };

  const register = async (input: RegisterInput) => {
    const result = await api.register(input);
    if (!result.data) {
      return { ok: false, error: result.problem?.detail ?? result.problem?.title ?? 'Could not create the account' };
    }
    const next = toState(result);
    if (next) publish(next);
    return { ok: true };
  };

  const loginDemo = (role: Role = 'lifter') => {
    publish(demoSessionFor(role));
  };

  const switchRole = (role: Role) => {
    const current = readSession();
    if (current.source !== 'demo' && current.user) {
      // On a real API a role change is a server-side upgrade; locally we only
      // ever change roles for the demo persona.
      return;
    }
    publish(demoSessionFor(role));
  };

  const switchTrack = (track: TrainingTrack) => {
    const current = readSession();
    const base = current.user ?? demoMe(primaryRoleOf(current.user));
    const meta = trackMeta(track);
    setDemoTrack(track);
    publish({
      ...current,
      user: { ...base, track, trackLabel: meta.label },
      source: current.source ?? 'demo',
    });
  };

  const updateUser = (patch: Partial<User>) => {
    const current = readSession();
    if (!current.user) return;
    publish({ ...current, user: { ...current.user, ...patch } });
  };

  const logout = () => {
    publish(EMPTY);
  };

  const user = session.user;

  return {
    session,
    user,
    source: session.source,
    isDemo: session.source === 'demo',
    isAuthenticated: Boolean(user),
    hasRole: (role: Role) => hasRole(user, role),
    primaryRole: primaryRoleOf(user),
    login,
    register,
    loginDemo,
    switchRole,
    switchTrack,
    updateUser,
    logout,
  };
}

/** Read-only helpers used outside React (router, api bootstrap). */
export const demoIdentities = { lifter: DEMO_LIFTER, trainer: DEMO_TRAINER, coach: DEMO_COACH };

export function isDemoSession(): boolean {
  return readSession().source === 'demo';
}
